<template>
  <div class="ap-portal">
    <Transition name="ap-fade">
      <div
        v-if="value"
        class="ap-popup"
        :style="{
          left:   pos.x + 'px',
          top:    pos.y + 'px',
          width:  size.w + 'px',
          height: size.h + 'px',
        }"
        ref="popupEl"
      >
 
        <!-- ══ HEADER ══════════════════════════════════════════════ -->
        <div class="ap-header" @mousedown="startDrag">
          <div class="ap-header__left">
            <div class="ap-header__icon-wrap">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <span class="ap-header__title">Anfitriones</span>
            <span v-if="meta" class="ap-header__count">{{ meta.total }}</span>
          </div>
          <button class="ap-close" @click="$emit('input', false)">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
 
        <!-- ══ SEARCH ═══════════════════════════════════════════════ -->
        <div class="ap-search-wrap">
          <svg class="ap-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="6.5" cy="6.5" r="5"/><path d="M10.5 10.5l3.5 3.5" stroke-linecap="round"/>
          </svg>
          <input
            class="ap-search"
            v-model="query"
            placeholder="Nombre, email, teléfono, ID…"
            @input="onInput"
            ref="searchEl"
          />
          <Transition name="ap-fade-quick">
            <button v-if="query" class="ap-search-clear" @click="query = ''; doFetch()">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </Transition>
        </div>
 
        <!-- ══ BODY ══════════════════════════════════════════════════ -->
        <div class="ap-body">
          <Transition name="ap-slide" mode="out-in">
 
            <!-- ── DETALLE ──────────────────────────────────────── -->
            <div v-if="detail" key="detail" class="ap-detail-wrap">
              <button class="ap-back" @click="detail = null">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Volver a la lista
              </button>
 
              <!-- Spinner mientras carga el detalle -->
              <div v-if="loadingDetail" class="ap-state">
                <span class="ap-spinner"></span>
                <span>Cargando…</span>
              </div>
 
              <!-- Contenido real una vez que llegaron los datos -->
              <div v-else class="ap-detail">
                <!-- Avatar hero -->
                <div class="ap-detail__hero">
                  <div class="ap-detail__avatar">{{ initials(detail.nombre, detail.apellido) }}</div>
                  <div class="ap-detail__hero-info">
                    <div class="ap-detail__name">{{ detail.nombre }} {{ detail.apellido }}</div>
                    <div class="ap-detail__id">#{{ detail.idusuario }}</div>
                    <div class="ap-detail__since">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      Anfitrión desde {{ formatDate(detail.fecha_registro) }}
                    </div>
                  </div>
                  <div v-if="filteredAlojamientos && filteredAlojamientos.length" class="ap-detail__aloj-badge">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    </svg>
                    {{ filteredAlojamientos.length }} aloj.
                  </div>
                </div>
 
                <!-- Info rows -->
                <div class="ap-detail__rows">
                  <div class="ap-detail__row">
                    <span class="ap-detail__row-icon">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    </span>
                    <span class="ap-detail__row-lbl">Email</span>
                    <span class="ap-detail__row-val">{{ detail.correo || '—' }}</span>
                  </div>
                  <div class="ap-detail__row">
                    <span class="ap-detail__row-icon">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                    </span>
                    <span class="ap-detail__row-lbl">Teléfono</span>
                    <span class="ap-detail__row-val">{{ detail.telefono || '—' }}</span>
                  </div>
                  <div class="ap-detail__row">
                    <span class="ap-detail__row-icon">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                    </span>
                    <span class="ap-detail__row-lbl">RUT</span>
                    <span class="ap-detail__row-val">{{ detail.rut || '—' }}</span>
                  </div>
                  <div class="ap-detail__row">
                    <span class="ap-detail__row-icon">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                    </span>
                    <span class="ap-detail__row-lbl">Idioma</span>
                    <span class="ap-detail__row-val">{{ (detail && detail.pref_lenguaje) ? detail.pref_lenguaje.toUpperCase() : '—' }}</span>
                  </div>
                  <div class="ap-detail__row">
                    <span class="ap-detail__row-icon">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                    </span>
                    <span class="ap-detail__row-lbl">Moneda</span>
                    <span class="ap-detail__row-val">{{ detail.pref_moneda || '—' }}</span>
                  </div>
                  <div>
                                      <!-- Alojamientos -->
                  <div class="ap-aloj-section">
                    <div class="ap-aloj-header">
                      <div class="ap-aloj-title">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                        </svg>
                        Alojamientos ({{ filteredAlojamientos.length }})
                      </div>
                      <input
                        class="ap-aloj-search"
                        v-model="alojQuery"
                        placeholder="ID o nombre…"
                      />
                    </div>
 
                    <div class="ap-aloj-list">
                      <div  v-if="filteredAlojamientos.length > 0">
                        <div
                          v-for="aloj in filteredAlojamientos"
                          :key="aloj.idcodigo"
                          class="ap-aloj-item"
                        >
                          <div class="ap-aloj-item__top">
                            <span class="ap-aloj-item__id">#{{ aloj.idcodigo }}</span>
                            <span class="ap-aloj-item__tipo">{{ aloj.tipo }}</span>
                            <span class="ap-aloj-item__tipo">{{ aloj.categoria }}</span>
                            <span
                              class="ap-aloj-item__status"
                              :class="aloj.aprobado && !aloj.eliminado ? 'ap-aloj-item__status--ok' : 'ap-aloj-item__status--off'"
                            >
                              {{ aloj.eliminado ? 'Eliminado' : aloj.aprobado ? 'Activo' : 'Pendiente' }}
                            </span>
                          </div>
                          <div class="ap-aloj-item__nombre">{{ localeName(aloj.nombre) }}</div>
                          <div class="ap-aloj-item__dir">{{ aloj.direccion }}</div>
                        </div>
                      </div >
                      <div v-else class="ap-aloj-empty">Sin resultados</div>
                    </div>
                  </div>
                  </div>
                </div>
 
                <div v-if="detail.biografia" class="ap-bio">
                  <div class="ap-bio__label">BIO</div>
                  <div class="ap-bio__text">{{ detail.biografia }}</div>
                </div>
              </div>
              

            </div>
            
 
            <!-- ── LISTA ────────────────────────────────────────── -->
            <div v-else key="list" class="ap-list-wrap">
              <div v-if="loading" class="ap-state">
                <span class="ap-spinner"></span>
                <span>Buscando…</span>
              </div>
              <div v-else-if="error" class="ap-state ap-state--error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {{ error }}
              </div>
              <div v-else-if="items.length === 0" class="ap-state">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity="0.25">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                </svg>
                Sin resultados
              </div>
              <div v-else class="ap-list">
                <div
                  v-for="(a, i) in items"
                  :key="a.idusuario"
                  class="ap-item"
                  :style="{ animationDelay: i * 30 + 'ms' }"
                  @click="openDetail(a.idusuario)"
                >
                  <div class="ap-item__avatar">{{ initials(a.nombre, a.apellido) }}</div>
                  <div class="ap-item__body">
                    <div class="ap-item__top">
                      <span class="ap-item__name">{{ a.nombre }} {{ a.apellido }}</span>
                      <span class="ap-item__id">#{{ a.idusuario }}</span>
                    </div>
                    <div class="ap-item__email">{{ a.correo }}</div>
                    <div class="ap-item__bottom">
                      <span class="ap-item__phone">{{ a.telefono }}</span>
                      <span v-if="a.alojamientos_count" class="ap-item__aloj">
                        🏠 {{ a.alojamientos_count }}
                      </span>
                    </div>
                  </div>
                  <svg class="ap-item__arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </div>
 
              <div v-if="meta && meta.last_page > 1" class="ap-pagination">
                <button :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <span>{{ meta.current_page }} <span class="ap-pag-sep">/</span> {{ meta.last_page }}</span>
                <button :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            </div>

            
 
          </Transition>
        </div>
 
        <!-- ══ RESIZE HANDLES ════════════════════════════════════════ -->
        <div class="ap-resize ap-resize--e"  @mousedown.stop="startResize($event, 'e')"></div>
        <div class="ap-resize ap-resize--s"  @mousedown.stop="startResize($event, 's')"></div>
        <div class="ap-resize ap-resize--w"  @mousedown.stop="startResize($event, 'w')"></div>
        <div class="ap-resize ap-resize--n"  @mousedown.stop="startResize($event, 'n')"></div>
        <div class="ap-resize ap-resize--se" @mousedown.stop="startResize($event, 'se')"></div>
        <div class="ap-resize ap-resize--sw" @mousedown.stop="startResize($event, 'sw')"></div>
        <div class="ap-resize ap-resize--ne" @mousedown.stop="startResize($event, 'ne')"></div>
        <div class="ap-resize ap-resize--nw" @mousedown.stop="startResize($event, 'nw')"></div>
 
      </div>
    </Transition>
  </div>
</template>

<script>
import apiUrls from '@/new-constants/apiUrls'
import { localeText as localeTextUtil } from '@/Utils/Utils'
export default {
  name: 'AnfitrionesPopup',
  props: {
    value: { type: Boolean, default: false },
    baseUrl:    { type: String,  default: apiUrls.base }
  },
  data() {
    return {
      items: [],
      meta: null,
      loading: false,
      loadingDetail: false,
      error: null,
      query: '',
      page: 1,
      detail: null,
      alojQuery: '',
      size: { w: 480, h: 560 },
      pos: { x: 0, y: 0 },
      // interaction state
      _dragging: false,
      _ox: 0,
      _oy: 0,
      _resizing: false,
      _debTimer: null
    }
  },
  computed: {
    filteredAlojamientos() {
      const aloj = (this.detail && Array.isArray(this.detail.alojamientos)) ? this.detail.alojamientos : []
      const q = this.alojQuery ? this.alojQuery.trim().toLowerCase() : ''
      if (!q) return aloj
      return aloj.filter(a => {
        const matchId = String(a.idcodigo ? a.idcodigo : '').includes(q)
        const matchNombre = this.localeName(a.nombre ? a.nombre : '').toLowerCase().includes(q)
        return matchId || matchNombre
      })
    }
  },
  watch: {
    value(v) {
      if (v) {
        this.detail = null
        this.query = ''
        this.page = 1
        this.pos = this.clampPos(
          window.innerWidth  / 2 - this.size.w / 2,
          window.innerHeight / 2 - this.size.h / 2,
          this.size.w,
          this.size.h
        )
        this.doFetch()
        this.$nextTick(() => { if (this.$refs.searchEl) this.$refs.searchEl.focus() })
      }
    }
  },
  methods: {
    MIN_W() { return 320 },
    MIN_H() { return 300 },
    MAX_W() { return window.innerWidth - 16 },
    MAX_H() { return window.innerHeight - 16 },
    clampPos(x, y, w, h) {
      const vw = window.innerWidth
      const vh = window.innerHeight
      return {
        x: Math.min(Math.max(x, 0), vw - w),
        y: Math.min(Math.max(y, 0), vh - h)
      }
    },
    startDrag(e) {
      if (e.target.closest('button, .ap-resize')) return
      this._dragging = true
      this._ox = e.clientX - this.pos.x
      this._oy = e.clientY - this.pos.y
      document.addEventListener('mousemove', this._onDrag)
      document.addEventListener('mouseup', this._stopDrag)
    },
    _onDrag(e) {
      if (!this._dragging) return
      this.pos = this.clampPos(e.clientX - this._ox, e.clientY - this._oy, this.size.w, this.size.h)
    },
    _stopDrag() {
      this._dragging = false
      document.removeEventListener('mousemove', this._onDrag)
      document.removeEventListener('mouseup', this._stopDrag)
    },
    startResize(e, dir) {
      e.preventDefault()
      this._resizing = true
      const startX = e.clientX, startY = e.clientY
      const startW = this.size.w, startH = this.size.h
      const startPX = this.pos.x, startPY = this.pos.y
      const onMove = (ev) => {
        if (!this._resizing) return
        const dx = ev.clientX - startX
        const dy = ev.clientY - startY
        let newW = startW, newH = startH, newX = startPX, newY = startPY
        if (dir.includes('e')) newW = startW + dx
        if (dir.includes('w')) { newW = startW - dx; newX = startPX + dx }
        if (dir.includes('s')) newH = startH + dy
        if (dir.includes('n')) { newH = startH - dy; newY = startPY + dy }
        newW = Math.min(Math.max(newW, this.MIN_W()), this.MAX_W())
        newH = Math.min(Math.max(newH, this.MIN_H()), this.MAX_H())
        if (dir.includes('w')) newX = startPX + startW - newW
        if (dir.includes('n')) newY = startPY + startH - newH
        this.size = { w: newW, h: newH }
        this.pos = this.clampPos(newX, newY, newW, newH)
      }
      const onUp = () => {
        this._resizing = false
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    onInput() {
      clearTimeout(this._debTimer)
      this._debTimer = setTimeout(() => {
        this.page = 1
        const q = this.query.trim()
        if (q && /^\d+$/.test(q)) {
          this.openDetail(Number(q))
        } else {
          this.doFetch()
        }
      }, 300)
    },
    async doFetch() {
      this.loading = true
      this.error = null
      try {
        const params = new URLSearchParams({ page: this.page, limit: 15 })
        if (this.query && this.query.trim()) params.set('q', this.query.trim())
        const res = await window.fetch(`${apiUrls.adminAnfitriones}?${params}`)
        const json = await res.json()
        this.items = json.data ? json.data : []
        this.meta  = json.meta ? json.meta : null
      } catch (e) {
        this.error = 'No se pudo conectar.'
      } finally {
        this.loading = false
      }
    },
    async openDetail(id) {
      console.log('Abriendo detalle de anfitrión', id)
      this.alojQuery = ''
      this.error = null
      this.detail = {}
      this.loadingDetail = true
      try {
        const res = await window.fetch(`${apiUrls.adminAnfitriones}/${id}`)
        const json = await res.json()
        this.detail = Object.assign({}, json.data, {
          alojamientos: Array.isArray(json.data && json.data.alojamientos) ? json.data.alojamientos : []
        })
      } catch (e) {
        this.error = 'Error cargando detalle.'
        this.detail = null
      } finally {
        console.log('Detalle cargado:', this.detail)
        this.loadingDetail = false
      }
    },
    changePage(p) { this.page = p; this.doFetch() },
    initials(n, a) {
      const first = (n && n[0]) ? n[0] : ''
      const last = (a && a[0]) ? a[0] : ''
      return ((first + last).toUpperCase()) || '?'
    },
    formatDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    localeName(v) { return localeTextUtil(v, (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'es') }
  }
}
</script>
 
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');
/* ─── TOKENS ────────────────────────────────────── */
.ap-popup {
  --primary:        #FC7010;
  --primary-light:  rgba(252,112,16,0.10);
  --primary-dim:    rgba(252,112,16,0.22);
  --primary-dark:   #B84800;
  --bg:             #ffffff;
  --bg-1:           #FAFAF9;
  --bg-2:           #F4F3F0;
  --border:         #ECEAE4;
  --border-str:     #DBD9D2;
  --txt:            #0A0A0B;
  --txt-2:          #383840;
  --txt-3:          #6E6E76;
  --txt-4:          #A0A0A8;
  --red:            #dc2626;
  --green:          #059669;
  --green-bg:       #f0fdf4;
  --green-bdr:      #a7f3d0;
}

/* ══ POPUP ══════════════════════════════════════════ */
.ap-popup {
  position: fixed;
  z-index: 1000;
  background: var(--bg);
  border: 1px solid var(--border-str);
  border-radius: 16px;
  box-shadow:
    0 4px 6px rgba(0,0,0,0.04),
    0 12px 32px rgba(0,0,0,0.10),
    0 32px 64px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Geist', 'DM Sans', 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  color: var(--txt);
}

/* ══ RESIZE HANDLES ════════════════════════════════ */
.ap-resize { position: absolute; z-index: 10; }
.ap-resize--e  { right: 0;  top: 8px;  bottom: 8px; width: 6px;  cursor: ew-resize; }
.ap-resize--w  { left: 0;   top: 8px;  bottom: 8px; width: 6px;  cursor: ew-resize; }
.ap-resize--s  { bottom: 0; left: 8px; right: 8px;  height: 6px; cursor: ns-resize; }
.ap-resize--n  { top: 0;    left: 8px; right: 8px;  height: 6px; cursor: ns-resize; }
.ap-resize--se { right: 0;  bottom: 0; width: 12px; height: 12px; cursor: nwse-resize; }
.ap-resize--sw { left: 0;   bottom: 0; width: 12px; height: 12px; cursor: nesw-resize; }
.ap-resize--ne { right: 0;  top: 0;    width: 12px; height: 12px; cursor: nesw-resize; }
.ap-resize--nw { left: 0;   top: 0;    width: 12px; height: 12px; cursor: nwse-resize; }

/* ══ HEADER ════════════════════════════════════════ */
.ap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 14px;
  background: #ffffff;
  border-bottom: 1px solid var(--border);
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
  position: relative;
}
.ap-header::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--primary) 0%, transparent 60%);
}
.ap-header:active { cursor: grabbing; }
.ap-header__left { display: flex; align-items: center; gap: 9px; }
.ap-header__icon-wrap {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-dark);
  flex-shrink: 0;
}
.ap-header__title {
  font-weight: 800;
  font-size: 14px;
  color: var(--txt);
  letter-spacing: -0.2px;
}
.ap-header__count {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--txt-3);
  background: #F4F3F0;
  border: 1px solid var(--border-str);
  padding: 2px 8px;
  border-radius: 20px;
}
.ap-close {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #FAFAF9;
  border: 1px solid var(--border-str);
  cursor: pointer;
  color: var(--txt-4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.ap-close:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: var(--red);
}

/* ══ SEARCH ════════════════════════════════════════ */
.ap-search-wrap {
  position: relative;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--bg-1);
}
.ap-search-icon {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  color: var(--txt-4);
  pointer-events: none;
}
.ap-search {
  width: 100%;
  background: #ffffff;
  border: 1px solid var(--border-str);
  border-radius: 9px;
  color: var(--txt);
  font-size: 12.5px;
  font-family: inherit;
  padding: 8px 28px 8px 28px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.ap-search::placeholder { color: var(--txt-4); }
.ap-search:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(252,112,16,0.12);
}
.ap-search-clear {
  position: absolute;
  right: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: #F4F3F0;
  border: none;
  cursor: pointer;
  color: var(--txt-3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.ap-search-clear:hover {
  background: var(--primary-light);
  color: var(--primary-dark);
}

/* ══ BODY ══════════════════════════════════════════ */
.ap-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ap-list-wrap,
.ap-detail-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* ══ LISTA ═════════════════════════════════════════ */
.ap-list { flex: 1; overflow-y: auto; }
.ap-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
  animation: ap-item-in 0.2s ease both;
}
@keyframes ap-item-in {
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
}
.ap-item:hover {
  background: #FAFAF9;
  transform: translateX(1px);
}
.ap-item:hover .ap-item__arrow {
  opacity: 1;
  transform: translateX(0);
}

.ap-item__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #ffffff;
}
.ap-item__body  { flex: 1; min-width: 0; }
.ap-item__top   { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 2px; }
.ap-item__name  { font-weight: 700; font-size: 13px; color: var(--txt); }
.ap-item__id    { font-size: 10.5px; color: var(--txt-4); flex-shrink: 0; margin-left: 6px; }
.ap-item__email { font-size: 11.5px; color: var(--txt-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 2px; }
.ap-item__bottom { display: flex; align-items: center; justify-content: space-between; }
.ap-item__phone { font-size: 11px; color: var(--txt-4); }
.ap-item__aloj {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--primary-dark);
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  padding: 2px 8px;
  border-radius: 20px;
}
.ap-item__arrow {
  color: var(--txt-4);
  flex-shrink: 0;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.15s ease;
}

/* ══ PAGINACIÓN ════════════════════════════════════ */
.ap-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 9px;
  border-top: 1px solid var(--border);
  font-size: 12px;
  color: var(--txt-3);
  flex-shrink: 0;
  background: var(--bg-1);
}
.ap-pag-sep { opacity: 0.4; margin: 0 2px; }
.ap-pagination button {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid var(--border-str);
  color: var(--txt-3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.ap-pagination button:hover:not(:disabled) {
  border-color: var(--primary-dim);
  color: var(--primary-dark);
  background: var(--primary-light);
}
.ap-pagination button:disabled { opacity: 0.3; cursor: not-allowed; }

/* ══ DETALLE ═══════════════════════════════════════ */
.ap-back {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-dark);
  background: var(--bg-1);
  border: none;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: background 0.15s;
  font-family: inherit;
  flex-shrink: 0;
}
.ap-back:hover { background: var(--primary-light); }

.ap-detail {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ap-detail__hero {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 14px;
  background: var(--bg-1);
  border: 1px solid var(--border-str);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}
.ap-detail__hero::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary) 0%, transparent 65%);
  border-radius: 12px 12px 0 0;
}

.ap-detail__avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%);
  border: 2px solid #FFD4AE;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: #ffffff;
}
.ap-detail__hero-info { flex: 1; }
.ap-detail__name {
  font-size: 15px;
  font-weight: 800;
  color: var(--txt);
  margin-bottom: 2px;
  letter-spacing: -0.2px;
}
.ap-detail__id {
  font-size: 11px;
  color: var(--txt-4);
  margin-bottom: 5px;
}
.ap-detail__since {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--txt-3);
}
.ap-detail__aloj-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--primary-dark);
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  padding: 4px 10px;
  border-radius: 20px;
  flex-shrink: 0;
  align-self: flex-start;
}

.ap-detail__rows { display: flex; flex-direction: column; }
.ap-detail__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 0;
  border-bottom: 1px solid var(--border);
  font-size: 12.5px;
}
.ap-detail__row:last-child { border-bottom: none; }
.ap-detail__row-icon { color: var(--txt-4); flex-shrink: 0; display: flex; align-items: center; }
.ap-detail__row-lbl  { color: var(--txt-3); font-weight: 600; min-width: 68px; flex-shrink: 0; font-size: 12px; }
.ap-detail__row-val  { color: var(--txt-2); flex: 1; text-align: right; word-break: break-all; font-size: 12px; }

/* ══ ALOJAMIENTOS ══════════════════════════════════ */
.ap-aloj-section {
  background: var(--bg-1);
  border: 1px solid var(--border-str);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 4px;
}

.ap-aloj-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--border);
  background: #F4F3F0;
}
.ap-aloj-title {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--txt-3);
}
.ap-aloj-search {
  background: #ffffff;
  border: 1px solid var(--border-str);
  border-radius: 7px;
  color: var(--txt);
  font-size: 11.5px;
  font-family: inherit;
  padding: 5px 9px;
  outline: none;
  width: 130px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.ap-aloj-search::placeholder { color: var(--txt-4); }
.ap-aloj-search:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(252,112,16,0.12);
}

.ap-aloj-list { display: flex; flex-direction: column; }

.ap-aloj-item {
  padding: 9px 12px;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 3px;
  transition: background 0.12s;
}
.ap-aloj-item:last-child { border-bottom: none; }
.ap-aloj-item:hover { background: #ffffff; }

.ap-aloj-item__top {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}
.ap-aloj-item__id {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--primary-dark);
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  padding: 2px 7px;
  border-radius: 20px;
}
.ap-aloj-item__tipo {
  font-size: 10px;
  font-weight: 600;
  color: var(--txt-3);
  background: #F4F3F0;
  border: 1px solid var(--border-str);
  padding: 2px 7px;
  border-radius: 20px;
  text-transform: capitalize;
}
.ap-aloj-item__status {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  margin-left: auto;
}
.ap-aloj-item__status--ok {
  color: var(--green);
  background: var(--green-bg);
  border: 1px solid var(--green-bdr);
}
.ap-aloj-item__status--off {
  color: var(--txt-4);
  background: #F4F3F0;
  border: 1px solid var(--border-str);
}

.ap-aloj-item__nombre {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--txt);
}
.ap-aloj-item__dir {
  font-size: 11px;
  color: var(--txt-4);
}
.ap-aloj-empty {
  padding: 14px;
  text-align: center;
  font-size: 12px;
  color: var(--txt-4);
}

/* ══ BIO ═══════════════════════════════════════════ */
.ap-bio {
  background: var(--bg-1);
  border: 1px solid var(--border-str);
  border-radius: 10px;
  padding: 12px;
}
.ap-bio__label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--txt-4);
  text-transform: uppercase;
  margin-bottom: 6px;
}
.ap-bio__text {
  font-size: 12.5px;
  color: var(--txt-3);
  line-height: 1.6;
}

/* ══ ESTADOS ═══════════════════════════════════════ */
.ap-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--txt-4);
  font-size: 13px;
  padding: 24px;
}
.ap-state--error { color: var(--red); }

.ap-spinner {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2.5px solid #ECEAE4;
  border-top-color: var(--primary);
  animation: ap-spin 0.75s linear infinite;
}
@keyframes ap-spin { to { transform: rotate(360deg); } }

/* ══ TRANSICIONES ══════════════════════════════════ */
.ap-fade-enter-active,
.ap-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.ap-fade-enter { opacity: 0; transform: scale(0.97) translateY(6px); }
.ap-fade-leave-to   { opacity: 0; transform: scale(0.97) translateY(6px); }

.ap-fade-quick-enter-active,
.ap-fade-quick-leave-active { transition: opacity 0.1s; }
.ap-fade-quick-enter,
.ap-fade-quick-leave-to { opacity: 0; }

.ap-slide-enter-active,
.ap-slide-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.ap-slide-enter { opacity: 0; transform: translateX(8px); }
.ap-slide-leave-to   { opacity: 0; transform: translateX(-8px); }

* { scrollbar-width: thin; scrollbar-color: #ECEAE4 transparent; }

/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE — el popup flotante pasa a pantalla completa en móvil
   (sobre-escribe el left/top/width/height inline con !important)
════════════════════════════════════════════════════════════════ */
@media (max-width: 640px) {
  .ap-popup {
    left: 0 !important;
    top: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    height: 100dvh !important;
    max-width: 100vw;
    border-radius: 0 !important;
  }
  /* Sin arrastrar ni redimensionar con el dedo */
  .ap-resize { display: none !important; }
  .ap-header { cursor: default; }
}
</style>